import React from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

// Mock departments data for preview
const departments = [
  { value: 'customer-service', label: 'Kundenservice' },
  { value: 'operations', label: 'Betrieb' },
  { value: 'manufacturing', label: 'Fertigung' },
  { value: 'administration', label: 'Verwaltung' },
  { value: 'security', label: 'Sicherheit' }
];

const PreviewPanel = ({ previewData, conflicts, suggestions }) => {
  // Mock employee data for suggestions
  const mockEmployees = [
    {
      id: 1,
      name: 'Anna Schmidt',
      avatar: 'https://images.unsplash.com/photo-1494790108755-2616b612b786?w=150',
      skills: ['customer-support', 'leadership'],
      availability: 'Verfügbar',
      matchScore: 95,
      hourlyRate: '€18/Std'
    },
    {
      id: 2,
      name: 'Michael Weber',
      avatar: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=150',
      skills: ['technical-skills', 'quality-control'],
      availability: 'Verfügbar',
      matchScore: 88,
      hourlyRate: '€22/Std'
    },
    {
      id: 3,
      name: 'Sarah Müller',
      avatar: 'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=150',
      skills: ['data-entry', 'customer-support'],
      availability: 'Teilweise verfügbar',
      matchScore: 76,
      hourlyRate: '€15/Std'
    }
  ];

  const mockConflicts = [
    {
      type: 'overlap',
      message: 'Überschneidung mit bestehender Schicht "Nachmittagsschicht" (14:00-18:00)',
      severity: 'warning'
    },
    {
      type: 'understaffed',
      message: 'Nur 2 von 3 benötigten Mitarbeitern verfügbar',
      severity: 'error'
    }
  ];

  const calculateShiftDuration = () => {
    if (!previewData?.startTime || !previewData?.endTime) return '0 Std';
    
    const start = new Date(`2000-01-01T${previewData.startTime}`);
    const end = new Date(`2000-01-01T${previewData.endTime}`);
    const duration = (end - start) / (1000 * 60 * 60);
    
    return `${duration} Std`;
  };

  const calculateTotalCost = () => {
    if (!previewData?.payGrade || !previewData?.maxEmployees) return '€0';
    
    const hourlyRates = {
      'entry': 12,
      'standard': 15,
      'senior': 18,
      'lead': 22,
      'supervisor': 25
    };
    
    const rate = hourlyRates?.[previewData?.payGrade] || 15;
    const duration = parseFloat(calculateShiftDuration()) || 0;
    const total = rate * duration * previewData?.maxEmployees;
    
    return `€${total?.toFixed(2)}`;
  };

  if (!previewData) {
    return (
      <div className="bg-card rounded-lg border border-border p-6">
        <div className="text-center text-muted-foreground">
          <Icon name="Eye" size={48} className="mx-auto mb-4 opacity-50" />
          <p>Füllen Sie das Formular aus, um eine Vorschau zu sehen</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Shift Overview */}
      <div className="bg-card rounded-lg border border-border p-6">
        <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center">
          <Icon name="Eye" size={20} className="mr-2" />
          Schichtvorschau
        </h3>

        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <p className="text-sm text-muted-foreground">Titel</p>
              <p className="font-medium text-foreground">{previewData?.title || 'Unbenannt'}</p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Abteilung</p>
              <p className="font-medium text-foreground">
                {previewData?.department ? 
                  departments?.find(d => d?.value === previewData?.department)?.label || previewData?.department 
                  : 'Nicht ausgewählt'
                }
              </p>
            </div>
          </div>

          <div className="grid grid-cols-3 gap-4">
            <div>
              <p className="text-sm text-muted-foreground">Datum</p>
              <p className="font-medium text-foreground">
                {previewData?.date ? new Date(previewData.date)?.toLocaleDateString('de-DE') : 'Nicht festgelegt'}
              </p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Zeit</p>
              <p className="font-medium text-foreground">
                {previewData?.startTime && previewData?.endTime 
                  ? `${previewData?.startTime} - ${previewData?.endTime}`
                  : 'Nicht festgelegt'
                }
              </p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Dauer</p>
              <p className="font-medium text-foreground">{calculateShiftDuration()}</p>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <p className="text-sm text-muted-foreground">Mitarbeiter</p>
              <p className="font-medium text-foreground">{previewData?.maxEmployees || 1} Person(en)</p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Geschätzte Kosten</p>
              <p className="font-medium text-foreground">{calculateTotalCost()}</p>
            </div>
          </div>

          {previewData?.requiredSkills && previewData?.requiredSkills?.length > 0 && (
            <div>
              <p className="text-sm text-muted-foreground mb-2">Erforderliche Fähigkeiten</p>
              <div className="flex flex-wrap gap-2">
                {previewData?.requiredSkills?.map((skill, index) => (
                  <span
                    key={index}
                    className="px-2 py-1 bg-accent/10 text-accent text-xs rounded-md"
                  >
                    {skill}
                  </span>
                ))}
              </div>
            </div>
          )}

          {previewData?.isRecurring && (
            <div className="bg-muted/50 rounded-md p-3">
              <div className="flex items-center mb-2">
                <Icon name="Repeat" size={16} className="mr-2 text-accent" />
                <span className="text-sm font-medium text-foreground">Wiederkehrende Schicht</span>
              </div>
              <p className="text-sm text-muted-foreground">
                {previewData?.recurringPattern} bis {previewData?.recurringEndDate ? 
                  new Date(previewData.recurringEndDate)?.toLocaleDateString('de-DE') : 
                  'Enddatum nicht festgelegt'
                }
              </p>
            </div>
          )}
        </div>
      </div>
      {/* Conflicts and Warnings */}
      {mockConflicts?.length > 0 && (
        <div className="bg-card rounded-lg border border-border p-6">
          <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center">
            <Icon name="AlertTriangle" size={20} className="mr-2 text-warning" />
            Konflikte & Warnungen
          </h3>

          <div className="space-y-3">
            {mockConflicts?.map((conflict, index) => (
              <div
                key={index}
                className={`flex items-start space-x-3 p-3 rounded-md ${
                  conflict?.severity === 'error' ?'bg-error/10 border border-error/20' :'bg-warning/10 border border-warning/20'
                }`}
              >
                <Icon
                  name={conflict?.severity === 'error' ? 'XCircle' : 'AlertCircle'}
                  size={16}
                  className={conflict?.severity === 'error' ? 'text-error mt-0.5' : 'text-warning mt-0.5'}
                />
                <p className="text-sm text-foreground">{conflict?.message}</p>
              </div>
            ))}
          </div>
        </div>
      )}
      {/* Employee Suggestions */}
      <div className="bg-card rounded-lg border border-border p-6">
        <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center">
          <Icon name="Users" size={20} className="mr-2" />
          Empfohlene Mitarbeiter
        </h3>

        <div className="space-y-3">
          {mockEmployees?.map((employee) => (
            <div
              key={employee?.id}
              className="flex items-center justify-between p-3 border border-border rounded-md hover:bg-muted/50 transition-colors"
            >
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-secondary rounded-full flex items-center justify-center overflow-hidden">
                  <img
                    src={employee?.avatar}
                    alt={employee?.name}
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      e.target.style.display = 'none';
                      e.target.nextSibling.style.display = 'flex';
                    }}
                  />
                  <Icon name="User" size={16} color="white" style={{ display: 'none' }} />
                </div>
                <div>
                  <p className="font-medium text-foreground">{employee?.name}</p>
                  <div className="flex items-center space-x-2 text-sm text-muted-foreground">
                    <span>{employee?.availability}</span>
                    <span>•</span>
                    <span>{employee?.hourlyRate}</span>
                  </div>
                </div>
              </div>

              <div className="flex items-center space-x-3">
                <div className="text-right">
                  <div className="flex items-center space-x-1">
                    <Icon name="Star" size={14} className="text-warning fill-current" />
                    <span className="text-sm font-medium text-foreground">{employee?.matchScore}%</span>
                  </div>
                  <p className="text-xs text-muted-foreground">Übereinstimmung</p>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  iconName="Plus"
                  iconPosition="left"
                  iconSize={14}
                >
                  Zuweisen
                </Button>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-4 pt-4 border-t border-border">
          <Button
            variant="ghost"
            iconName="Search"
            iconPosition="left"
            iconSize={16}
            className="w-full"
          >
            Weitere Mitarbeiter suchen
          </Button>
        </div>
      </div>
      {/* Coverage Analysis */}
      <div className="bg-card rounded-lg border border-border p-6">
        <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center">
          <Icon name="BarChart3" size={20} className="mr-2" />
          Abdeckungsanalyse
        </h3>

        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <span className="text-sm text-muted-foreground">Personalbesetzung</span>
            <div className="flex items-center space-x-2">
              <div className="w-24 h-2 bg-muted rounded-full overflow-hidden">
                <div className="w-2/3 h-full bg-success"></div>
              </div>
              <span className="text-sm font-medium text-foreground">67%</span>
            </div>
          </div>

          <div className="flex items-center justify-between">
            <span className="text-sm text-muted-foreground">Fähigkeitsabdeckung</span>
            <div className="flex items-center space-x-2">
              <div className="w-24 h-2 bg-muted rounded-full overflow-hidden">
                <div className="w-5/6 h-full bg-success"></div>
              </div>
              <span className="text-sm font-medium text-foreground">83%</span>
            </div>
          </div>

          <div className="flex items-center justify-between">
            <span className="text-sm text-muted-foreground">Kosteneffizienz</span>
            <div className="flex items-center space-x-2">
              <div className="w-24 h-2 bg-muted rounded-full overflow-hidden">
                <div className="w-3/4 h-full bg-warning"></div>
              </div>
              <span className="text-sm font-medium text-foreground">75%</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PreviewPanel;